import { QrCodeIcon } from "@heroicons/react/16/solid";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { CurrencyDollarIcon } from "@heroicons/react/24/outline";
import { useCallback } from "react";
import { AttributionBadge } from "@/core/components/attribution-badge";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { formatPhoneNumber } from "@/core/utils/phone";
import { ACTIVE_REFERRAL_STATUSES } from "@/referrals/constants";
import { IReferral } from "@/referrals/referral";
import { ReferralOptionsDropdown } from "./referral-options-dropdown";
import { SidebarBadge } from "./sidebar-badge";

interface SidebarHeaderProps {
  referral: IReferral;
  actionButton: React.ReactNode;
  onClose: () => void;
  onPayout: () => void;
  onLost?: () => void;
  onMoveToPaid?: () => void;
  hideIconPayoutButton?: boolean;
  onDelete?: () => void;
}

export const SidebarHeader = ({
  referral,
  actionButton,
  onClose,
  onPayout,
  onLost,
  onMoveToPaid,
  hideIconPayoutButton = false,
  onDelete,
}: SidebarHeaderProps) => {
  const signupSource = referral.advocate.signup_source;

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Stack gap="lg" className="bg-bg-secondary/50 p-6" fullWidth>
      <Stack fullWidth>
        <Stack direction="row" justify="between" align="center" fullWidth>
          <Stack direction="row" gap="sm" align="center">
            <SidebarBadge referral={referral} />
            {signupSource && (
              <AttributionBadge
                icon={<QrCodeIcon className="h-4 w-4" />}
                signupSource={signupSource}
                section="referrals"
              />
            )}
          </Stack>
          <XMarkIcon className="h-6 w-6 cursor-pointer" onClick={handleClose} />
        </Stack>
        <Stack gap="sm">
          <Typography className="text-2xl font-bold">
            {referral.potential_customer.name || "Unnamed Referral"}
          </Typography>
          <Typography className="text-xl">
            {formatPhoneNumber(referral.potential_customer.phone_number) ||
              "No phone number"}
          </Typography>
        </Stack>
      </Stack>

      <Stack direction="row" fullWidth>
        {actionButton}
        {!hideIconPayoutButton &&
          ACTIVE_REFERRAL_STATUSES.includes(referral.status) && (
            <Button variant="icon" size="icon" type="button" onClick={onPayout}>
              <CurrencyDollarIcon />
            </Button>
          )}
        <ReferralOptionsDropdown
          referral={referral}
          onLost={onLost}
          onMoveToPaid={onMoveToPaid}
          onDelete={onDelete}
        />
      </Stack>
    </Stack>
  );
};
